import React, { useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import {
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  LocalPharmacy as PharmacyIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment";

export default function MedicationAdministration({ patient, encounter }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();
  const [administerDialogOpen, setAdministerDialogOpen] = useState(false);
  const [selectedPrescriptionDetail, setSelectedPrescriptionDetail] = useState(null);

  const {
    data: pendingMeds,
    isLoading: pendingLoading,
    isError: pendingError,
  } = useQuery({
    queryKey: ["pending-medications", patient?.id],
    queryFn: () =>
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/medication-administrations/pending/?patient=${patient?.id}`,
          {
            headers: { Authorization: `Token ${token}` },
          }
        )
        .then((res) => res.data),
    enabled: !!token && !!patient?.id,
  });

  const {
    data: historyData,
    isLoading: historyLoading,
  } = useQuery({
    queryKey: ["medication-administrations", patient?.id],
    queryFn: () =>
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/medication-administrations/?patient=${patient?.id}`,
          {
            headers: { Authorization: `Token ${token}` },
          }
        )
        .then((res) => res.data),
    enabled: !!token && !!patient?.id,
  });

  const history = historyData?.results || historyData || [];

  if (!patient) {
    return (
      <Alert severity="info">
        {t("nurse.selectPatientForMedications", "Please select a patient to view medications.")}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
      <Typography variant="h6" gutterBottom>
        {t("nurse.medicationAdministration", "Medication Administration")}
      </Typography>

      {/* Pending Medications */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t("nurse.pendingMedications", "Pending Medications")}
        </Typography>
        {pendingLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : pendingError ? (
          <Alert severity="error">
            {t("nurse.errorLoadingPending", "Failed to load pending medications.")}
          </Alert>
        ) : pendingMeds && pendingMeds.length > 0 ? (
          <Grid container spacing={2}>
            {pendingMeds.map((med, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {med.medication_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("nurse.dosage", "Dosage")}: {med.dosage} • {t("nurse.quantity", "Qty")}: {med.quantity}
                        </Typography>
                        {med.notation && (
                          <Typography variant="body2" color="text.secondary">
                            {med.notation}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CheckIcon />}
                        onClick={() => {
                          setSelectedPrescriptionDetail(med);
                          setAdministerDialogOpen(true);
                        }}
                      >
                        {t("nurse.markGiven", "Mark Given")}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info">
            {t("nurse.noPendingMedications", "No pending medications for this patient.")}
          </Alert>
        )}
      </Paper>

      {/* Administration History */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t("nurse.administrationHistory", "Administration History")}
        </Typography>
        {historyLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : history.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("nurse.medication", "Medication")}</TableCell>
                  <TableCell>{t("nurse.status", "Status")}</TableCell>
                  <TableCell>{t("nurse.administeredAt", "Administered At")}</TableCell>
                  <TableCell>{t("nurse.nurse", "Nurse")}</TableCell>
                  <TableCell>{t("nurse.notes", "Notes")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell>{admin.medication_name || admin.prescription_detail_display}</TableCell>
                    <TableCell>
                      <Chip
                        label={admin.status_display}
                        size="small"
                        color={
                          admin.status === "given" ? "success" :
                          admin.status === "missed" ? "error" :
                          admin.status === "refused" ? "warning" : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {moment(admin.administered_at).format("YYYY-MM-DD HH:mm")}
                    </TableCell>
                    <TableCell>{admin.nurse_display}</TableCell>
                    <TableCell>{admin.notes || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">
            {t("nurse.noAdministrationHistory", "No medication administration history.")}
          </Alert>
        )}
      </Paper>

      <AdministerMedicationDialog
        open={administerDialogOpen}
        onClose={() => {
          setAdministerDialogOpen(false);
          setSelectedPrescriptionDetail(null);
          queryClient.invalidateQueries(["pending-medications"]);
          queryClient.invalidateQueries(["medication-administrations"]);
        }}
        prescriptionDetail={selectedPrescriptionDetail}
        patient={patient}
        encounter={encounter}
      />
    </Box>
  );
}

function AdministerMedicationDialog({ open, onClose, prescriptionDetail, patient, encounter }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    status: "given",
    quantity_given: "",
    notes: "",
    reason: "",
  });
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (open && prescriptionDetail) {
      setFormData({
        status: "given",
        quantity_given: prescriptionDetail.quantity || "",
        notes: "",
        reason: "",
      });
      setErrors({});
    }
  }, [open, prescriptionDetail]);

  const administerMedication = useMutation({
    mutationFn: (data) =>
      axios.post(`${process.env.REACT_APP_API_URL}/medication-administrations/`, data, {
        headers: { Authorization: `Token ${token}` },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["pending-medications"]);
      queryClient.invalidateQueries(["medication-administrations"]);
      if (encounter) {
        queryClient.invalidateQueries(["encounters", encounter.id]);
      }
      onClose();
    },
    onError: (error) => {
      console.error("Failed to record medication administration:", error);
      setErrors(error.response?.data || { non_field_errors: "Failed to record administration." });
    },
  });

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    setErrors({ ...errors, [field]: "" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!prescriptionDetail || !patient) return;

    const submitData = {
      prescription_detail: prescriptionDetail.prescription_detail_id,
      patient: patient.id,
      encounter: encounter?.id || null,
      nurse: auth?.user?.user?.id,
      status: formData.status,
      quantity_given: formData.status === "given" ? (formData.quantity_given || prescriptionDetail.quantity) : null,
      notes: formData.notes,
      reason: formData.status !== "given" ? formData.reason : null,
    };

    administerMedication.mutate(submitData);
  };

  if (!prescriptionDetail) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("nurse.recordMedication", "Record Medication Administration")}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {administerMedication.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.non_field_errors || administerMedication.error?.message || t("nurse.errors.genericError", "An error occurred.")}
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold">
              {prescriptionDetail.medication_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("nurse.dosage", "Dosage")}: {prescriptionDetail.dosage} • {t("nurse.quantity", "Quantity")}: {prescriptionDetail.quantity}
            </Typography>
          </Box>

          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="status-label">{t("nurse.status", "Status")}</InputLabel>
            <Select
              labelId="status-label"
              id="status-select"
              value={formData.status}
              label={t("nurse.status", "Status")}
              onChange={handleChange("status")}
            >
              <MenuItem value="given">{t("nurse.given", "Given")}</MenuItem>
              <MenuItem value="missed">{t("nurse.missed", "Missed")}</MenuItem>
              <MenuItem value="refused">{t("nurse.refused", "Refused")}</MenuItem>
              <MenuItem value="held">{t("nurse.held", "Held")}</MenuItem>
              <MenuItem value="partial">{t("nurse.partial", "Partial")}</MenuItem>
            </Select>
          </FormControl>

          {formData.status === "given" && (
            <TextField
              fullWidth
              type="number"
              margin="dense"
              label={t("nurse.quantityGiven", "Quantity Given")}
              value={formData.quantity_given}
              onChange={handleChange("quantity_given")}
              error={!!errors.quantity_given}
              helperText={errors.quantity_given}
            />
          )}

          {formData.status !== "given" && (
            <TextField
              fullWidth
              multiline
              rows={3}
              margin="dense"
              label={t("nurse.reason", "Reason")}
              value={formData.reason}
              onChange={handleChange("reason")}
              error={!!errors.reason}
              helperText={errors.reason}
              required
            />
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            margin="dense"
            label={t("nurse.notes", "Notes (Optional)")}
            value={formData.notes}
            onChange={handleChange("notes")}
            error={!!errors.notes}
            helperText={errors.notes}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={administerMedication.isPending}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={administerMedication.isPending ? <CircularProgress size={20} /> : <CheckIcon />}
            disabled={administerMedication.isPending}
          >
            {administerMedication.isPending ? t("common.saving", "Saving...") : t("common.save", "Save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
